export interface Comment {
  user: string;
  comment: string;
  timestamp: string;
}

export interface MultiComment {
  User: string;
  Comment: string;
  Date: string;
  /** When true, include this comment on the excursion sortie PDF (person-chip comments). */
  Important?: boolean;
}

function pickRawCommentDate(o: Record<string, unknown>): unknown {
  return o.Date ?? o.date ?? o.timestamp ?? o.Timestamp ?? o.created_at ?? o.CreatedAt;
}

/** Produces an ISO string when possible; otherwise a trimmed string for display fallback. */
function coerceMultiCommentDate(raw: unknown): string {
  const fallbackNow = () => new Date().toISOString();
  if (raw == null) return fallbackNow();
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? fallbackNow() : d.toISOString();
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return fallbackNow();
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    const spaced = s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
    const d2 = new Date(spaced);
    if (!Number.isNaN(d2.getTime())) return d2.toISOString();
    return s;
  }
  return fallbackNow();
}

/** Safe label for UI when `Date` may be missing, non-ISO, or legacy API shapes. */
export function formatMultiCommentDateForDisplay(dateStr: string | undefined | null): string {
  if (dateStr == null || typeof dateStr !== "string") return "—";
  const d = new Date(dateStr);
  if (!Number.isNaN(d.getTime())) return d.toLocaleString("fr-FR");
  const t = dateStr.trim();
  return t.length > 0 ? t : "—";
}

/** Maps API / DB JSON (snake-ish keys) and in-memory PascalCase into MultiComment. */
export function normalizeMultiComment(raw: unknown): MultiComment {
  const o = raw as Record<string, unknown>;
  const user = String(o.User ?? o.user ?? "");
  const comment = String(o.Comment ?? o.comment ?? "");
  const date = coerceMultiCommentDate(pickRawCommentDate(o));
  const importantRaw = o.Important ?? o.important;
  const important = importantRaw === true;

  return {
    User: user,
    Comment: comment,
    Date: date,
    ...(important ? { Important: true } : {}),
  };
}

export function normalizeMultiCommentList(raw: unknown[] | undefined | null): MultiComment[] {
  if (raw == null || raw.length === 0) return [];

  return raw.map((item) => normalizeMultiComment(item));
}
